<template>
    <el-dialog 
      title="Image Verification" 
      :visible.sync="verificationDialog" 
      width="80%" 
      top="5vh"
      :modal="true"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      @close="handleClose"
      @open="fetchVerificationImage"
      class="verification-dialog"
    >
      <div v-loading="loading" class="verification-content">
        <!-- Verification Info Grid -->
        <div class="verification-info">
          <div class="info-item">
            <span class="info-label">Status:</span>
            <el-tag :type="getStatusType(selectedSubmission.image_verification.approved)">
              {{ getStatusText(selectedSubmission.image_verification.approved) }}
            </el-tag>
          </div>
          <div class="info-item">
            <span class="info-label">Confidence Score:</span>
            <el-progress 
              :percentage="selectedSubmission.image_verification.ai_confidence" 
              :color="getProgressColor(selectedSubmission.image_verification.ai_confidence)"
              :text-inside="true"
              :stroke-width="20"
            />
          </div>
          <div class="info-item">
            <span class="info-label">Submission ID:</span>
            <span class="info-value">{{ selectedSubmission.id }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Verification Date:</span>
            <span class="info-value">
              {{ selectedSubmission.image_verification.verified_at ? moment(selectedSubmission.image_verification.verified_at).format('lll') : 'N/A' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">Task Type:</span>
            <span class="info-value">
             <a :href="selectedSubmission.task.target_url" target="_blank" rel="noopener noreferrer">
              {{ selectedSubmission.task.task_type ? selectedSubmission.task.task_type.name : 'Image Verification' }}
             </a>
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">AI Model:</span>
            <span class="info-value">{{ selectedSubmission.aiModel || 'Gemini-Flash v2.0' }}</span>
          </div>
        </div>

        <!-- Image Display -->
        <div class="image-container">
          <div class="image-wrapper" @click="openImageViewer">
            <img v-if="imageUrl" :src="imageUrl" class="verification-image" @error="handleImageError" />
            <div v-else class="image-placeholder">
              <i class="el-icon-picture-outline"></i>
              <p>No image available</p>
            </div>
            <div v-if="imageUrl" class="zoom-hint">
              <i class="el-icon-zoom-in"></i>
              Click to view full size
            </div>
          </div>
        </div>

        <!-- Reason Input -->
        <div v-if="selectedSubmission.image_verification.ai_confidence > 70" class="reason-section">
          <el-input
            v-model="selectedSubmission.image_verification.reason"
            type="textarea"
            :rows="3"
            placeholder="Enter reason for approval or rejection..."
            maxlength="500"
            show-word-limit
          />
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <el-button type="primary" @click="fetchVerificationImage" :loading="loading">
            <i class="el-icon-download"></i> {{ imageUrl ? 'Refresh Image' : 'Load Image' }}
          </el-button>
          <el-button v-if="selectedSubmission.image_verification.ai_confidence <= 70" type="success" @click="approveVerification">
            <i class="el-icon-check"></i> Approve
          </el-button>
          <el-button v-if="selectedSubmission.image_verification.ai_confidence > 70" type="danger" @click="rejectVerification">
            <i class="el-icon-close"></i> Reject
          </el-button>
        </div>
      </div>
        <!-- Full Size Image Viewer -->
      <el-dialog
        :visible.sync="showImageViewer"
        :show-close="true"
        :close-on-click-modal="true"
        :append-to-body="true"
        custom-class="image-viewer-dialog"
        width="90%"
      >
        <img 
          v-if="imageUrl" 
          :src="imageUrl" 
          class="full-size-image"
          @click="showImageViewer = false"
        />
      </el-dialog>
    </el-dialog>
  </template>
  
  <script>
  import { mapActions, mapState, mapMutations } from 'vuex'
  import moment from 'moment'
  
  export default {
    data() {
      return {
        loading: false,
        imageUrl: null,
        isZoomed: false,
        showImageViewer: false,
        moment: moment
      }
    },

    computed: {
      ...mapState('submissions', ['selectedSubmission', 'verificationDialog'])
    },
  
    methods: {
      ...mapActions('submissions', ['getVerificationImage', 'changeStatus']),
      ...mapMutations('submissions', ['setVerificationDialog']),

      async fetchVerificationImage() {
        this.loading = true
        try {
          const response = await this.getVerificationImage(this.selectedSubmission.image_verification.id)
          if (response && response.data) {
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' })
            this.imageUrl = URL.createObjectURL(blob)
          }
        } catch (error) {
          this.$message.error('Failed to load verification image')
        } finally {
          this.loading = false
        }
      },

      openImageViewer() {
        if (this.imageUrl) {
          this.showImageViewer = true
        }
      },

      handleImageError() {
        this.imageUrl = null
        this.$message.error('Failed to display image')
      },

      getStatusType(status) {
        const statusMap = {
          '1': 'success',
          '2': 'danger',
        }
        return statusMap[status] || 'info'
      },

      getStatusText(status) {
        const statusMap = {
          '1': 'Approved',
          '2': 'Rejected',
        }
        return statusMap[status] || 'Pending'
      },

      getProgressColor(percentage) {
        if (percentage >= 80) return '#67C23A'
        if (percentage >= 60) return '#E6A23C'
        return '#F56C6C'
      },

      async approveVerification() {
        await this.changeStatus({
          id: this.selectedSubmission.image_verification.id,
          status: 1,
          reason: this.selectedSubmission.image_verification.reason
        })
        this.setVerificationDialog(false)
      },

      async rejectVerification() {
        await this.changeStatus({
          id: this.selectedSubmission.image_verification.id,
          status: 2,
          reason: this.selectedSubmission.image_verification.reason
        })
        this.setVerificationDialog(false)
      },

      handleClose() {
        this.showImageViewer = false
        if (this.imageUrl) {
          URL.revokeObjectURL(this.imageUrl)
          this.imageUrl = null
        }
        this.setVerificationDialog(false)
      }
    },

    beforeDestroy() {
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl)
      }
    }
  }
  </script>

<style scoped>
.verification-dialog {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.verification-content {
  min-height: 400px;
}

.verification-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-weight: 600;
  color: #606266;
  font-size: 14px;
}

.info-value {
  color: #303133;
  font-size: 14px;
}

.image-container {
  margin: 20px 0;
  text-align: center;
}

.image-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
}

.verification-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.verification-image:hover {
  transform: scale(1.05);
}

.image-placeholder {
  width: 400px;
  height: 300px;
  background-color: #f5f7fa;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.image-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.zoom-hint {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.reason-section {
  margin: 20px 0;
  padding: 0 20px;
}

.reason-section .el-textarea__inner {
  font-family: inherit;
}

.action-buttons {
  margin-top: 20px;
  text-align: center;
  padding: 20px 0;
  border-top: 1px solid #ebeef5;
}

.action-buttons .el-button {
  margin: 0 10px;
}

.el-progress {
  width: 200px;
}

@media (max-width: 768px) {
  .verification-info {
    grid-template-columns: 1fr;
  }
  
  .image-wrapper {
    max-width: 100%;
  }
  
  .verification-image {
    max-width: 100%;
  }
}
</style>

<style>
.image-viewer-dialog {
  background-color: rgba(0, 0, 0, 0.9);
}

.image-viewer-dialog .el-dialog__header {
  display: none;
}

.image-viewer-dialog .el-dialog__body {
  padding: 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
}

.full-size-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  cursor: pointer;
}
</style>